import { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from "react-leaflet";
import axios from "axios";
import { API, STADIA_TILE_URL, hospitalIcon, emergencyIcon } from "./utils";
import { HospitalPopup } from "./HospitalPopup";
import { UnitGpsLayer } from "@/components/gps/UnitGpsMarker";
import { GpsUnitList } from "@/components/gps/GpsUnitList";
import { useNow, STATUS_COLORS } from "@/components/gps/gpsUtils";

function MapFocus({ target }) {
  const map = useMap();
  useEffect(() => { if (target) map.flyTo([target.lat, target.lng], Math.max(map.getZoom(), 16), { duration: 0.8 }); }, [target, map]);
  return null;
}
import { HydrantLayer } from "./HydrantLayer";

export function GPSMapScreen({ gpsPositions }) {
  const [hospitals, setHospitals] = useState([]);
  // Only VAC Coverage Areas render as a visual overlay. Precincts /
  // boroughs / sectors are stored for geofencing/dispatch logic but
  // intentionally NOT drawn — keeps the dispatcher map readable.
  const [vacBoundaries, setVacBoundaries] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);
  const defaultCenter = [40.6782, -73.9442];
  const now = useNow(1000);
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/hospitals`).then((r) => setHospitals(r.data.filter((h) => h.lat && h.lng))).catch(() => {});
    axios.get(`${API}/kml/vac-response`).then((r) => setVacBoundaries(r.data)).catch(() => {});
  }, []);

  // Poll active jobs every 5s so new emergency pins show up as calls come in.
  useEffect(() => {
    const load = () => {
      axios.get(`${API}/jobs`).then((r) => {
        const active = (r.data || []).filter((j) =>
          j.lat && j.lng && !["completed", "cancelled"].includes(j.status)
        );
        setActiveJobs(active);
      }).catch(() => {});
    };
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <div style={{ flex: 1, display: "flex", minHeight: 0 }}>
    <div style={{ flex: 1, position: "relative" }}>
      <MapContainer center={defaultCenter} zoom={12} style={{ width: "100%", height: "100%" }} zoomControl={true} attributionControl={false}>
        <TileLayer url={STADIA_TILE_URL} />
        <HydrantLayer />
        {vacBoundaries.map((vac) => {
          const positions = (vac.boundaries || []).map((b) => [b.lat, b.lng]);
          return positions.length >= 3 ? (
            <Polygon key={vac.id} positions={positions}
              pathOptions={{ color: "#BFD7EA", fillColor: "#BFD7EA", fillOpacity: 0.18, weight: 1.5 }}>
              <Popup><strong>{vac.name}</strong></Popup>
            </Polygon>
          ) : null;
        })}
        {hospitals.map((h) => <Marker key={h.id} position={[h.lat, h.lng]} icon={hospitalIcon}><Popup><HospitalPopup h={h} /></Popup></Marker>)}
        {activeJobs.map((j) => (
          <Marker key={j.id} position={[j.lat, j.lng]} icon={emergencyIcon}>
            <Popup>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 12 }}>
                <strong>CAD {j.cad_number}</strong><br/>
                {j.call_type}<br/>
                {j.location}{j.apt ? `, APT ${j.apt}` : ""}<br/>
                {j.assigned_unit_name && <span>Unit: {j.assigned_unit_name}</span>}
              </div>
            </Popup>
          </Marker>
        ))}
        <UnitGpsLayer positions={gpsPositions} now={now} />
        <MapFocus target={focus} />
      </MapContainer>
      <div style={{ position: "absolute", bottom: 10, left: 10, zIndex: 1000, background: "rgba(0,0,0,0.8)", padding: "8px 12px", borderRadius: 4, fontSize: 11, color: "#FFF", fontFamily: "'IBM Plex Mono', monospace" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div style={{ width: 14, height: 14, background: "#FF0000", border: "1px solid #FFF", borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#FFF", fontSize: 10, fontWeight: 700 }}>+</span></div>
          <span>Hospital</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
          <div style={{ width: 14, height: 14, background: "#1E90FF", border: "1px solid #FFF", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}><span style={{ color: "#FFF", fontSize: 10, fontWeight: 700 }}>+</span></div>
          <span>Emergency Call</span>
        </div>
        {Object.entries(STATUS_COLORS).map(([k, c]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 2 }}>
            <div style={{ width: 14, height: 9, background: c, border: "1px solid #000", borderRadius: 2 }} />
            <span>Ambulance — {k}</span>
          </div>
        ))}
      </div>
    </div>
    <GpsUnitList positions={gpsPositions} now={now} onFocus={(p) => setFocus({ lat: p.latitude, lng: p.longitude, t: Date.now() })} />
    </div>
  );
}
